/**
 * Copyright (c) 2026 Stefan Radig
 *
 * An untrusted server's page in the Tauri app has every Tauri command rejected
 * by the ACL (see the trusted-servers capability). Features that need native
 * commands must degrade instead of break: this module tells them whether
 * native commands work on this page and recognises a refusal when one happens.
 * In a plain browser (no __TAURI__) nothing changes: probeNative() is false
 * there and callers keep their existing browser behaviour.
 */
import { DebugLogger } from './debugLogger';
import { NativeRequestError } from './nativeBridge';

type Invoke = (command: string, args?: Record<string, unknown>) => Promise<unknown>;

function tauriInvoke(): Invoke | undefined {
    return (window as unknown as { __TAURI__?: { core?: { invoke?: Invoke } } }).__TAURI__?.core?.invoke;
}

/**
 * True when a rejection is the ACL refusing a command. Release builds say
 * "Command <cmd> not allowed by ACL"; debug builds give the longer
 * resolve_access_message ("... not allowed on window ...", "... not allowed on
 * origin [...]", "... not allowed. <permission details>").
 */
export function isNativeRefusal(error: unknown): boolean {
    // The Android bridges (nativeBridge.ts) refuse with their own code.
    if (error instanceof NativeRequestError) return error.code === 'not-allowed';
    const text = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
    return /not allowed( by ACL| on (window|origin|any)|\.)/.test(text);
}

const loggedKinds = new Set<string>();

/** Log one line per refusal kind (never the error text, which can hold URLs). */
export function logNativeRefusal(kind: string): void {
    if (loggedKinds.has(kind)) return;
    loggedKinds.add(kind);
    DebugLogger.log('state', `Native feature unavailable on this server (${kind}): not trusted for native access`);
}

let probe: Promise<boolean> | null = null;

/**
 * Whether native commands work on this page, probed once with a harmless
 * command (`plugin:os|platform`, granted to trusted servers) and cached.
 * A failure other than a refusal means the command is reachable, so native
 * stays available and the caller's own error handling deals with it.
 */
export function probeNative(): Promise<boolean> {
    probe ??= (async () => {
        const invoke = tauriInvoke();
        if (!invoke) return false;
        try {
            await invoke('plugin:os|platform');
            return true;
        } catch (error) {
            if (isNativeRefusal(error)) {
                logNativeRefusal('probe');
                return false;
            }
            return true;
        }
    })();
    return probe;
}

/** Forget the cached probe and logged kinds (tests). */
export function resetNativeProbe(): void {
    probe = null;
    loggedKinds.clear();
}
